// ─── Template: big-number-v1 ───────────────────────────────────────────────
// One huge number with a small label above and a subtitle below. Animates
// the number counting up from 0 to its target. Great for stats, prices,
// percentages, "+50% off"-style hooks.

import React from 'react';
import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

export type AlignPreset = 'left' | 'center' | 'right';
const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export type BigNumberProps = {
  /** Number to count up to. Stored as string so the user can enter "+50",
   *  "1,200", etc; we parse the digits for the count animation but render
   *  the original string with the live count substituted in. */
  value: string;
  /** Optional prefix shown before the number (e.g. "$", "+"). */
  prefix: string;
  /** Optional suffix shown after the number (e.g. "%", "k", " años"). */
  suffix: string;
  /** Small label above the number. */
  label: string;
  /** Subtitle below the number. */
  subtitle: string;
  // Number styling
  numberFont: string;
  numberSize: number;
  numberColor: string;
  numberBold: boolean;
  numberItalic: boolean;
  numberAlign: AlignPreset;
  // Label styling
  labelFont: string;
  labelSize: number;
  labelColor: string;
  labelBold: boolean;
  labelItalic: boolean;
  labelAlign: AlignPreset;
  // Subtitle styling
  subtitleFont: string;
  subtitleSize: number;
  subtitleColor: string;
  subtitleBold: boolean;
  subtitleItalic: boolean;
  subtitleAlign: AlignPreset;
  background: string;
  /** When true, the number counts up from 0 instead of just fading in. */
  countUp: boolean;
};

const Component: React.FC<BigNumberProps> = ({
  value, prefix, suffix, label, subtitle,
  numberFont, numberSize, numberColor, numberBold, numberItalic, numberAlign,
  labelFont, labelSize, labelColor, labelBold, labelItalic, labelAlign,
  subtitleFont, subtitleSize, subtitleColor, subtitleBold, subtitleItalic, subtitleAlign,
  background, countUp,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  // Tamaños calibrados sobre el lado más corto: garantiza que un
  // mismo % se vea igual en horizontal y vertical.
  const refDim = Math.min(width, height);

  // Parse the numeric portion of the string. We support commas/dots as
  // group separators by stripping them, and a leading sign.
  const targetNumber = parseFloat((value || '').replace(/[,_\s]/g, '').replace(/[^\d.\-]/g, ''));
  const hasNumber = Number.isFinite(targetNumber);

  const count = countUp && hasNumber
    ? interpolate(frame, [4, 28], [0, targetNumber], {
        easing: Easing.out(Easing.cubic),
        extrapolateRight: 'clamp',
      })
    : targetNumber;

  // Match the original formatting: integers stay integers, decimals keep
  // their precision. Reads the source string to detect.
  const decimals = (value.split('.')[1] || '').length;
  const display = hasNumber
    ? count.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : value;

  const labelOp = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: 'clamp' });
  const numberOp = interpolate(frame, [4, 18], [0, 1], { extrapolateRight: 'clamp' });
  const subOp = interpolate(frame, [24, 36], [0, 1], { extrapolateRight: 'clamp' });
  const numberY = interpolate(frame, [4, 18], [20, 0], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });

  const numberPx = (numberSize / 100) * refDim;
  const labelPx = (labelSize / 100) * refDim;
  const subtitlePx = (subtitleSize / 100) * refDim;

  return (
    <AbsoluteFill style={{
      background, fontFamily: FONT_STACK,
      alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
      padding: '0 6%',
    }}>
      {label && (
        <span style={{
          fontFamily: `${labelFont}, ${FONT_STACK}`,
          fontSize: labelPx,
          color: labelColor,
          fontWeight: labelBold ? 700 : 500,
          fontStyle: labelItalic ? 'italic' : 'normal',
          textAlign: labelAlign,
          letterSpacing: 2,
          textTransform: 'uppercase',
          opacity: labelOp,
          marginBottom: numberPx * 0.15,
        }}>
          {label}
        </span>
      )}
      <span style={{
        fontFamily: `${numberFont}, ${FONT_STACK}`,
        fontSize: numberPx,
        color: numberColor,
        fontWeight: numberBold ? 800 : 600,
        fontStyle: numberItalic ? 'italic' : 'normal',
        textAlign: numberAlign,
        lineHeight: 0.95,
        letterSpacing: -3,
        opacity: numberOp,
        transform: `translateY(${numberY}px)`,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {prefix}{display}{suffix}
      </span>
      {subtitle && (
        <span style={{
          fontFamily: `${subtitleFont}, ${FONT_STACK}`,
          fontSize: subtitlePx,
          color: subtitleColor,
          fontWeight: subtitleBold ? 700 : 400,
          fontStyle: subtitleItalic ? 'italic' : 'normal',
          textAlign: subtitleAlign,
          opacity: subOp,
          marginTop: numberPx * 0.15,
        }}>
          {subtitle}
        </span>
      )}
    </AbsoluteFill>
  );
};

const ALIGN_OPTIONS = [
  { value: 'left',   label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right',  label: 'Derecha' },
];

export const bigNumberTemplate: AnimationTemplate<BigNumberProps> = {
  id: 'big-number-v1',
  label: 'Número grande',
  description: 'Cifra destacada con etiqueta arriba y subtítulo abajo.',
  category: 'text',
  defaultDurationSec: 3,
  defaultProps: {
    value: '50',
    prefix: '+',
    suffix: '%',
    label: 'Crecimiento',
    // Texto de ejemplo en castellano para los valores por defecto.
    subtitle: 'En el último trimestre',
    numberFont: 'Inter', numberSize: 22, numberColor: '#FFFFFF',
    numberBold: true, numberItalic: false, numberAlign: 'center',
    labelFont: 'Inter', labelSize: 1.6, labelColor: '#A3A3A3',
    labelBold: true, labelItalic: false, labelAlign: 'center',
    subtitleFont: 'Inter', subtitleSize: 2.2, subtitleColor: '#FFFFFF',
    subtitleBold: false, subtitleItalic: false, subtitleAlign: 'center',
    background: '#0B0F1A',
    countUp: true,
  },
  propsSchema: {
    value:    { type: 'text', label: 'Número', placeholder: '50', agentExposed: true,
                styling: { font: 'numberFont', size: 'numberSize', color: 'numberColor',
                  bold: 'numberBold', italic: 'numberItalic', align: 'numberAlign' } },
    // Prefijo y sufijo comparten estilo con el número principal —
    // visualmente se renderizan junto al value, tiene sentido que
    // editar tipografía/color desde cualquiera de los tres modifique
    // el mismo conjunto de props.
    prefix:   { type: 'text', label: 'Prefijo', placeholder: 'Ej: $, +', agentExposed: true,
                styling: { font: 'numberFont', size: 'numberSize', color: 'numberColor',
                  bold: 'numberBold', italic: 'numberItalic', align: 'numberAlign' } },
    suffix:   { type: 'text', label: 'Sufijo', placeholder: 'Ej: %, k', agentExposed: true,
                styling: { font: 'numberFont', size: 'numberSize', color: 'numberColor',
                  bold: 'numberBold', italic: 'numberItalic', align: 'numberAlign' } },
    label:    { type: 'text', label: 'Etiqueta', placeholder: 'Texto chico arriba', agentExposed: true,
                styling: { font: 'labelFont', size: 'labelSize', color: 'labelColor',
                  bold: 'labelBold', italic: 'labelItalic', align: 'labelAlign' } },
    subtitle: { type: 'text', label: 'Subtítulo', placeholder: 'Texto debajo', agentExposed: true,
                styling: { font: 'subtitleFont', size: 'subtitleSize', color: 'subtitleColor',
                  bold: 'subtitleBold', italic: 'subtitleItalic', align: 'subtitleAlign' } },
    // Number styles
    numberFont:   { type: 'font',    label: 'Tipografía' },
    numberSize:   { type: 'number',  label: 'Tamaño', min: 8, max: 40, step: 0.5, decimals: 1, unit: '%' },
    numberColor:  { type: 'color',   label: 'Color' },
    numberBold:   { type: 'boolean', label: 'Negrita' },
    numberItalic: { type: 'boolean', label: 'Cursiva' },
    numberAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    // Label styles
    labelFont:    { type: 'font',    label: 'Tipografía' },
    labelSize:    { type: 'number',  label: 'Tamaño', min: 1, max: 5, step: 0.1, decimals: 1, unit: '%' },
    labelColor:   { type: 'color',   label: 'Color' },
    labelBold:    { type: 'boolean', label: 'Negrita' },
    labelItalic:  { type: 'boolean', label: 'Cursiva' },
    labelAlign:   { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    // Subtitle styles
    subtitleFont:   { type: 'font',    label: 'Tipografía' },
    subtitleSize:   { type: 'number',  label: 'Tamaño', min: 1, max: 6, step: 0.1, decimals: 1, unit: '%' },
    subtitleColor:  { type: 'color',   label: 'Color' },
    subtitleBold:   { type: 'boolean', label: 'Negrita' },
    subtitleItalic: { type: 'boolean', label: 'Cursiva' },
    subtitleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    // Scene
    background: { type: 'color',  label: 'Fondo', allowTransparent: true },
    countUp:    { type: 'boolean', label: 'Animar el conteo' },
  },
  Component,
};
